package com.trevorwiebe.timesheet.core.data

import com.trevorwiebe.timesheet.core.data.Utils.errorWrapper
import com.trevorwiebe.timesheet.core.domain.CoreRepository
import com.trevorwiebe.timesheet.core.domain.HttpInterface
import com.trevorwiebe.timesheet.core.domain.TSResult
import com.trevorwiebe.timesheet.core.domain.dto.DatabaseUserDto
import com.trevorwiebe.timesheet.core.domain.dto.HolidayDto
import com.trevorwiebe.timesheet.core.domain.dto.OrganizationDto
import com.trevorwiebe.timesheet.core.domain.dto.toDatabaseUserModel
import com.trevorwiebe.timesheet.core.domain.model.Organization
import com.trevorwiebe.timesheet.core.domain.model.toHoliday
import com.trevorwiebe.timesheet.core.domain.model.toOrganization
import com.trevorwiebe.timesheet.core.domain.model.toOrganizationDto
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.auth.FirebaseUser
import dev.gitlive.firebase.auth.auth
import dev.gitlive.firebase.firestore.firestore
import kotlinx.coroutines.flow.first
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.jsonObject
import kotlin.io.encoding.Base64
import kotlin.io.encoding.ExperimentalEncodingApi

class CoreRepositoryImpl(
    private val firebaseDatabase: Firebase,
    private val httpInterface: HttpInterface,
) : CoreRepository {

    override suspend fun getFirebaseUser(): TSResult {
        return errorWrapper {
            val firebaseAuth = firebaseDatabase.auth
            // On web the SDK restores the session asynchronously after page load,
            // so currentUser is null until the first authStateChanged emission.
            val firebaseUser = firebaseAuth.authStateChanged.first()
            if (firebaseUser == null) {
                TSResult(error = "User was null")
            } else {
                TSResult(data = firebaseUser)
            }
        }
    }

    override suspend fun getDatabaseUser(): TSResult {
        return errorWrapper {
            val organizationIdResult = getOrganizationId()
            if (organizationIdResult.error != null) return@errorWrapper organizationIdResult
            val organizationId = organizationIdResult.data as String

            val userIdResult = getUserId()
            if (userIdResult.error != null) return@errorWrapper userIdResult
            val userId = userIdResult.data as String

            val user = firebaseDatabase.firestore
                .collection("organizations")
                .document(organizationId)
                .collection("users")
                .document(userId)

            val databaseModel = user.get().data<DatabaseUserDto>().toDatabaseUserModel(user.id)
            TSResult(data = databaseModel)
        }
    }

    override suspend fun getUserList(includeDeleted: Boolean): TSResult {
        return errorWrapper {
            val organizationIdResult = getOrganizationId()
            if (organizationIdResult.error != null) return@errorWrapper organizationIdResult
            val organizationId = organizationIdResult.data as String
            val users = firebaseDatabase.firestore
                .collection("organizations")
                .document(organizationId)
                .collection("users")
                .get()
                .documents
                .map { document ->
                    document.data<DatabaseUserDto>()
                        .toDatabaseUserModel(document.id)
                }
                .filter { includeDeleted || !it.deleted }

            TSResult(data = users)
        }
    }


    override suspend fun getOrganization(): TSResult {
        return errorWrapper {
            val organizationIdResult = getOrganizationId()
            if (organizationIdResult.error != null) return@errorWrapper organizationIdResult
            val organizationId = organizationIdResult.data as String

            val organization = firebaseDatabase.firestore
                .collection("organizations")
                .document(organizationId)
                .get()

            TSResult(
                data = organization.data<OrganizationDto>().toOrganization(id = organizationId)
            )
        }
    }

    override suspend fun updateOrganization(organization: Organization): TSResult {
        return errorWrapper {
            val organizationIdResult = getOrganizationId()
            if (organizationIdResult.error != null) return@errorWrapper organizationIdResult
            val organizationId = organizationIdResult.data as String

            firebaseDatabase.firestore
                .collection("organizations")
                .document(organizationId)
                .set(organization.toOrganizationDto(), merge = true)

            TSResult(data = "Organization saved")
        }
    }

    @OptIn(ExperimentalEncodingApi::class)
    override suspend fun getOrganizationId(): TSResult {
        val tsResult = getFirebaseUser()
        if (tsResult.error != null) return tsResult
        val firebaseUser = tsResult.data as FirebaseUser

        // First try the cached token. If the organizationId custom claim is missing
        // (e.g. the claim was set after the cached token was minted, or right after a
        // cold launch), force a refresh once before giving up.
        extractOrganizationId(firebaseUser, forceRefresh = false)?.let {
            return TSResult(data = it)
        }
        extractOrganizationId(firebaseUser, forceRefresh = true)?.let {
            return TSResult(data = it)
        }
        return TSResult(error = "Organization ID was null")
    }

    @OptIn(ExperimentalEncodingApi::class)
    private suspend fun extractOrganizationId(
        firebaseUser: FirebaseUser,
        forceRefresh: Boolean,
    ): String? {
        val idTokenResult = firebaseUser.getIdTokenResult(forceRefresh)
        val idToken = idTokenResult.token ?: return null

        val parts = idToken.split(".")
        if (parts.size < 2) throw IllegalArgumentException("Invalid JWT token")

        var payloadBase64 = parts[1]
        payloadBase64 = payloadBase64
            .replace('-', '+')  // Convert URL-safe base64 to standard base64
            .replace('_', '/')  // Convert URL-safe base64 to standard base64
            .padEnd((payloadBase64.length + 3) / 4 * 4, '=') // Fix padding

        val payload = Base64.decode(payloadBase64).decodeToString()

        val claims = Json.parseToJsonElement(payload)
        val organizationId = claims.jsonObject["organizationId"]?.toString()

        return organizationId
            ?.removePrefix("\"")
            ?.removeSuffix("\"")
    }

    override suspend fun getUserId(): TSResult {
        val tsResult = getFirebaseUser()
        if (tsResult.error != null) return tsResult
        val firebaseUser = tsResult.data as FirebaseUser

        return TSResult(data = firebaseUser.uid)
    }

    override suspend fun signOut(): TSResult {
        return errorWrapper {
            val firebaseAuth = firebaseDatabase.auth
            val result = firebaseAuth.signOut()
            TSResult(data = result)
        }
    }

    @Suppress("UNCHECKED_CAST")
    override suspend fun getHolidays(year: String, countryCode: String): TSResult {
        return errorWrapper {
            val result = httpInterface.getHolidays(year, countryCode)
            val holidays = (result.data as? List<HolidayDto>)?.map { it.toHoliday() } ?: emptyList()

            // Filter the holidays to only include the ones we want
            val chosenHolidays = listOf(
                "New Year's Day",
                "Memorial Day",
                "Independence Day",
                "Labor Day",
                "Thanksgiving Day",
                "Christmas Day"
            )
            result.copy(data = holidays.filter { chosenHolidays.contains(it.name) })
        }
    }
}